<template>
  <div>
    <div v-if="getProduct.isInShop">
      <div class="product-header">
        <div class="product-preview">
          <div class="shop-breadcrumb">
            <NuxtLink class="breadcrumb--back" :to="getRouteToHome()">{{ $t("konf_Back") }}</NuxtLink>
            <NuxtLink class="breadcrumb--link" :to="getRouteToHome()">Home</NuxtLink>
            <span class="breadcrumb--divider">/</span>
            <span class="breadcrumb--current">{{ getProduct.nameImShop }}</span>
          </div>
          <div class="preview-image">
            <ClientOnly>
            <inner-image-zoom
                :src="productImageUnZoomed"
                :alt="imageAltText(getProduct.nameImShop)" :title="imageAltText(getProduct.nameImShop)"
                :zoomSrc="getProduct.previewPath +'cdn-cgi/image/fit=cover,width=1000,height=1000/' + getProduct.saveID + '_' + imageIndex + '.png'"
                className="preview-zoom"
            />
            </ClientOnly>
            <span class="turn-left previewchanger" @click="rotateImage()"></span>
          </div>
        </div>
        <div class="product-content">
          <h1 class="product-content--title">{{ getProduct.nameImShop }}</h1>
          <h4 class="product-content--subtittle">{{ getProduct.produkt }}</h4>
          <div class="product-content--description">
            <ProductViewDetails :details="getProduct.features"/>
          </div>
          <div class="product-content--price">
            <span>{{ $n(parseFloat(getProduct.shopPrice), {key: 'currency', currency: waehrung}) }}</span>
            <span>{{ $t("konfi_inklMWST_only") }} </span>
          </div>
          <div class="product-content--deliverytime">{{ $t("shop_lieferzeit") }}</div>
          <div class="product-content--personalize" v-if="getProduct.isPersonalisierbar">
            <ExemplaryData :product="getProduct"/>
          </div>
          <ProductViewCurrentSizes v-if="countSizes>1" :product="getProduct" :sizeMissing="sizeMissing"
                                   @resetErrors="resetErrors"/>
          <ClientOnly>
            <div class="product-content--goessenguide" @click="changeToGroessenTab()">
              <span class="icon-sizes"></span>
              <span class="text-sizes">{{ $t("shop_groessenguide") }}</span>
            </div>
            <div class="product-content--btn">
              <span v-if="!savingToWarenkorb" class="km__button--standard blue" @click="inWarenkorbLegen()">{{ $t("konf_toBasket") }}</span>
              <span v-else class="km__button--standard blue"><SpinningCircleAnimation/></span>
            </div>
          </ClientOnly>
        </div>
      </div>
      <div id="tabs" class="product-content-more">
        <Tabs :initialTab="$t('details')" ref="buttonRef">
          <Tab :name="$t('details')">
            <ProductViewFeatures :features="getProduktFeatures"/>
          </Tab>
          <Tab :name="$t('Groessen')">
            <ProductViewSizeExplained :produkt="getProduct.auwiProdukt" :sport="getProduct.sport"/>
          </Tab>
        </Tabs>
      </div>

    </div>
    <div v-if="!getProduct.isInShop">
      <FourOFour/>
    </div>
  </div>
</template>

<script lang="ts">

import {computed, type Ref, ref, watch} from "vue";
import {useRoute} from 'vue-router';
import ProductViewDetails from "~/components/ProductViewDetails.vue";
import ProductViewFeatures from "~/components/ProductViewFeatures.vue";
import ProductViewSizeExplained from "~/components/ProductViewSizeExplained.vue";
import ProductViewCurrentSizes from "~/components/ProductViewCurrentSizes.vue";
import ExemplaryData from "~/components/ExemplaryData.vue";
import Tabs from '@global/components/Tabs.vue';
import Tab from '@global/components/Tab.vue'
import {getRouteToHome} from "~/_router/routeUtils";
import {useI18n} from "vue-i18n";
import 'vue-inner-image-zoom/lib/vue-inner-image-zoom.css';
import InnerImageZoom from 'vue-inner-image-zoom';
import FourOFour from "~/components/FourOFour.vue";
import {type ShopItem, useShopItems} from "~/store/shopItemsStore";
import {useProductStore} from "~/store/productstore";
import {useShopInfos} from "~/store/shopInfosStore";
import LoadingComponent from "~/components/LoadingComponent.vue";
import {setHeadTags} from "~/metatags/metatags.js";
import {useShopSeiten} from "~/store/shopSeitenStore";
import {insertCloudflareDownscalingIntoURL} from "@k/global/globalUtils";



export default {
  name: "ProductDetail",
  components: {
    LoadingComponent,

    FourOFour,
    ExemplaryData,
    ProductViewCurrentSizes,
    ProductViewDetails,
    ProductViewFeatures,
    ProductViewSizeExplained,
    Tabs,
    Tab,
    'inner-image-zoom': InnerImageZoom,

  },
  async setup() {
    const route = useRoute();
    const i18n = useI18n();
    const sizeMissing = ref(false);
    const imageIndex = ref(0);
    const buttonRef = ref(null);
    const shopItems = useShopItems();
    const shopseite = useShopSeiten();
    const shopinfos = useShopInfos();
    const leaving = ref(false);
    const savingToWarenkorb = ref(false);
    onBeforeUnmount(()=>{
      leaving.value = true;
    })
    const allShopItems = computed(() => shopItems.allShopItems);
    const shopUUID = route.params.id;
    const allFormerItems = computed(() => shopItems.allFormerItems);
    const getProduct = computed(() => {
      let produkt = allShopItems.value.shopitems.find(item => item.shopUUID === shopUUID) as ShopItem|undefined;
      if (produkt === undefined) {
        produkt = allFormerItems.value.formerShopItems.find(item => item.ShopUUID === shopUUID) as ShopItem|undefined;
      }
      if (produkt === undefined && !leaving.value) {
        throw createError({
          statusCode: 404,
          message: 'not found',
          fatal: true
        })
      }
      return produkt;
    }) as Ref<ShopItem>;
    const getProduktFeatures = computed(() => getProduct.value.features.map(object => object.feature).join(','));
    const productStore = useProductStore();
    const shopInfos = useShopInfos();
    const productImageUnZoomed = computed(()=>{
      return insertCloudflareDownscalingIntoURL(getProduct.value.previewPath + getProduct.value.saveID + '_' + imageIndex.value + '.png', 550, 550);
    }    )
    const url = useRequestURL();

    setHeadTags(
        shopInfos.getShopSprache,
        shopInfos.getShopLand,
        shopinfos.shopname,
        shopseite.getProduktSeite?.BeschreibungContent || "",
        getProduct.value.previewPath + getProduct.value.saveID + '_0.png',
        {
          shopUUID: getProduct.value.shopUUID,
          preis: getProduct.value.shopPrice,
          currencyCode: shopInfos.getStandardWaehrung
        },
        url.toString()
    );

    if (getProduct.value.isInShop) {
      productStore.loadSizesInfo({sport: getProduct.value.sport, webproduct: getProduct.value.webproduct});
      productStore.loadProductDetails({sport: getProduct.value.sport, webproduct: getProduct.value.webproduct});
      // generateJsonLd(shopInfos.shopname, getProduct.value.saveID, getProduct.value.shopPrice, getProduct.value.shopUUID).then(response => {
      //   useHead({
      //     script: [
      //       {
      //         hid: "productinfo",
      //         type: "application/ld+json",
      //         children: response
      //       }
      //     ]
      //   })
      // });
    }

    watch(() => getProduct.value, (currentProduct) => {
      if (currentProduct.isInShop) {
        productStore.loadSizesInfo({sport: currentProduct.sport, webproduct: currentProduct.webproduct});
        productStore.loadProductDetails({sport: currentProduct.sport, webproduct: currentProduct.webproduct});
        // useJsonld(() => generateJsonLd(shopInfos.shopname, getProduct.value.saveID, getProduct.value.shopPrice, getProduct.value.shopUUID) as JsonLD)
      }
    });

    function resetErrors() {
      sizeMissing.value = false;
    }

    function rotateImage() {
      resetErrors();
      imageIndex.value = (imageIndex.value === 3) ? 0 : imageIndex.value + 1;
    }

    async function inWarenkorbLegenOhnePersonalisierung() {
      if (shopItems.getGewaehlteGroesse(getProduct.value.saveID) === "Choose your size") {
        sizeMissing.value = true;
      } else {
        savingToWarenkorb.value = true;
        await shopItems.saveItemInWarenkorb({
          item: getProduct.value,
          size: shopItems.getGewaehlteGroesse(getProduct.value.saveID),
          shop: shopInfos.shopname
        });
        savingToWarenkorb.value = false;
      }
    }

    async function inWarenkorbLegenMitPersonalisierung() {
      if (shopItems.getGewaehlteGroesse(getProduct.value.saveID) === "Choose your size") {
        sizeMissing.value = true;
      } else {
        sizeMissing.value = false;
        savingToWarenkorb.value = true;
        await shopItems.savePersonalisiertesItemInWarenkorb({item: getProduct.value, shop: shopInfos.shopname});
        savingToWarenkorb.value = false;
      }
    }

    function inWarenkorbLegen() {
      resetErrors();
      getProduct.value.isPersonalisierbar ? inWarenkorbLegenMitPersonalisierung() : inWarenkorbLegenOhnePersonalisierung();
    }

    function imageAltText(produktname:string) {
      return i18n.t("shop_Produktansicht") + ' ' + shopInfos.shopname + ' ' + produktname;
    }

    function changeToGroessenTab() {
      (buttonRef.value as {clickAButtonFromParent:(s:string)=>void}).clickAButtonFromParent(i18n.t('Groessen'));
      const section = document.getElementById("tabs");
      const offsetTop = section.offsetTop;
      window.scrollTo({
        top: offsetTop,
        behavior: 'smooth'
      });
    }

    return {
      sizeMissing,
      imageIndex,
      allShopItems,
      allFormerItems,
      shopUUID,
      getProduct,
      resetErrors,
      rotateImage,
      inWarenkorbLegenOhnePersonalisierung,
      inWarenkorbLegen,
      getProduktFeatures,
      getRouteToHome,
      imageAltText,
      buttonRef,
      changeToGroessenTab,
      countSizes: computed(() => getProduct.value.possibleSizes.length),
      waehrung: computed(() => shopInfos.getStandardWaehrung),
      isDevMode: import.meta.env.DEV,
      productImageUnZoomed,
      savingToWarenkorb
    }
  }
}
</script>

<style lang="scss" scoped>

input {
  display: block;
  border: none;
  background: $color-grey;
  padding: 6px 10px;
  width: 280px;
  margin-bottom: 10px;
  margin-top: 5px;
}

.turn-left {
  position: absolute;
  bottom: 10px;
  left: 50%;
  width: 30px;
  height: 30px;
  background-image: url("https://static.owayo-cdn.com/konfigurator_html/img/layout/toolbox-drehen-rechts.svg");
  background-size: contain;
  background-repeat: no-repeat;
  margin-left: -15px;
  z-index: 9;
}

.product-content {
  width: calc(100% - 30px);
  margin-left: 30px;

  h3 {
    font-weight: 700;
    text-transform: none;
  }

  &--title {
    font-weight: 700;
    margin-top: 20px;
  }

  &--subtittle {
    margin-bottom: 20px;
  }

  &--price {
    margin-bottom: 5px;

    span {
      &:first-child {
        font-weight: 600;
        font-size: 16px;
        display: inline-block;
      }

      &:last-child {
        display: inline-block;
        padding-left: 5px;
      }
    }
  }

  &--deliverytime {
    margin-bottom: 30px;
  }

  &--personalize {
    margin-bottom: 25px;
  }

  &--order-btn {
    background: $color-red;
    width: 200px;
    text-align: center;
    color: white;
    padding: 6px 10px;
    display: block;
    border-radius: 5px;
    cursor: pointer;
    font-weight: 600;
  }

  &--back-btn {
    background: $color-blue;
    width: 200px;
    text-align: center;
    color: white;
    padding: 6px 10px;
    display: block;
    border-radius: 5px;
    cursor: pointer;
    font-weight: 600;
  }

  &--goessenguide {
    cursor: pointer;
  }
}

.preview-image {
  width: calc(100% - 30px);
  margin-right: 30px;
  /*border: 1px solid $color-grey;*/
  background: #ececec;
  border-radius: 5px;
  height: 550px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;

  img {
    object-fit: contain;
    max-height: 470px;
  }

  @media (max-width: 940px) {
    height: 470px;

    img {
      width: 100%;
    }
  }

  @media (max-width: 740px) {
    height: auto;

    img {
      max-width: 400px;
      padding: 30px;
    }
  }
}

.product-header {
  display: flex;
  flex-direction: row;
  padding-bottom: 60px;
  position: relative;

  .product-preview {
    width: 65%;
    position: relative;

    @media (max-width: 940px) {
      width: 50%;
    }
  }

  .product-content {
    width: 35%;

    @media (max-width: 940px) {
      width: 50%;
      margin-left: 0;
    }
  }

  @media (max-width: 740px) {
    flex-direction: column;

    .product-preview {
      width: 100%;
    }

    .product-content {
      width: 100%;
    }
  }
}

.product-content--btn {
  display: flex;
  justify-content: space-between;

  .km__button--standard {
    width: 100%;
    height: 40px;
    margin-right: 15px;
  }
}

.product-content--goessenguide {
  display: block;
  margin-bottom: 20px;

  .icon-sizes {
    display: inline-block;
    width: 28px;
    height: 10px;
    background-image: url("data:image/svg+xml;base64,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");
    background-repeat: no-repeat;
    background-size: 28px auto;
    background-position: 0 center;
    transform: scaleY(-1);
    position: relative;
    top: 1px;
  }

  .text-sizes {
    display: inline-block;
    border-bottom: 1px solid $color-black;
    margin-left: 5px;
  }
}

</style>
